import { lazy, Suspense, useEffect } from 'react'
import { Box, Confetti } from '@factorialco/deprecated-design-system'
import { F0Button, F0Dialog } from '@factorialco/f0-react'
import { Calendar } from '@factorialco/f0-react/icons/app'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import i18n from 'modules/core/lib/i18n'
import { track } from 'modules/core/lib/Tracking'
import { events } from 'modules/get_started/lib/events'
import { useDemoTour } from 'modules/get_started/hooks/useDemoTour'
import { useDemoTourTasks } from 'modules/get_started/hooks/useDemoTourTasks'
import DemoTourPopover from 'modules/get_started/components/DemoTourPopover'

const UpsellMeeting = lazy(() =>
  import('modules/upsell/components/UpsellMeeting').then((m) => ({ default: m.UpsellMeeting }))
)

const WelcomeModal = ({
  onStart,
  onDismiss,
  onClose,
}: {
  onStart: () => void
  onDismiss: () => void
  onClose: () => void
}) => (
  <F0Dialog isOpen onClose={onClose} width="sm">
    <Box alignItems="center" paddingX="s32" paddingBottom="s32" paddingTop="s16">
      <h2 className="text-center text-3xl font-semibold mt-2">
        {i18n.t('growth.get_started.demo_tour.welcome_title')}
      </h2>

      <p className="text-center text-lg text-f1-foreground-secondary mt-3">
        {i18n.t('growth.get_started.demo_tour.welcome_description')}
      </p>

      <Box gap="s16" style={{ marginTop: '32px' }}>
        <F0Button
          variant="default"
          size="lg"
          label={i18n.t('growth.get_started.demo_tour.start_tour')}
          onClick={onStart}
        />
        <F0Button
          variant="ghost"
          size="lg"
          label={i18n.t('growth.get_started.demo_tour.explore_on_my_own')}
          onClick={onDismiss}
        />
      </Box>
    </Box>
  </F0Dialog>
)

const DEMO_AVATAR_URL = 'https://www.datocms-assets.com/58969/1775005778-demo-avatar.png'

const BookingModal = ({
  allCompleted,
  onContinue,
  onBook,
  onClose,
}: {
  allCompleted: boolean
  onContinue: (() => void) | null
  onBook: () => void
  onClose: () => void
}) => (
  <F0Dialog
    isOpen
    onClose={onClose}
    width="sm"
    title={
      allCompleted
        ? i18n.t('growth.get_started.demo_tour.tour_complete_title')
        : i18n.t('growth.get_started.demo_tour.step_completed')
    }
  >
    <Box component="article" flexDirection="row" alignItems="flexStart" gap="s12" padding="s16">
      <img src={DEMO_AVATAR_URL} alt="" width={45} height={45} className="shrink-0 rounded-full" />
      <Box flexDirection="column">
        <h3 className="text-lg font-semibold text-f1-foreground">
          {i18n.t('growth.get_started.demo_tour.book_meeting_title')}
        </h3>
        <p className="mt-2 text-base text-f1-foreground-secondary">
          {i18n.t('growth.get_started.demo_tour.book_meeting_description')}
        </p>
      </Box>
    </Box>

    <Box
      component="footer"
      flexDirection="row"
      alignItems="center"
      justifyContent="flexEnd"
      gap="s12"
      padding="s16"
      style={{ marginTop: '24px' }}
    >
      {onContinue ? (
        <F0Button
          variant="outline"
          label={i18n.t('growth.get_started.demo_tour.continue_tour')}
          onClick={onContinue}
        />
      ) : null}
      <F0Button
        variant="default"
        label={i18n.t('growth.get_started.demo_tour.book_meeting')}
        icon={Calendar}
        onClick={onBook}
      />
    </Box>
  </F0Dialog>
)

const DemoAiTours = () => {
  const { has_show_ai_demo_tours: showAiDemoTours } = useFeatures(['show_ai_demo_tours'])
  const { tasks } = useDemoTourTasks()
  const {
    view,
    activeTaskKey,
    completedTasks,
    currentStep,
    showConfetti,
    calendarUrl,
    meetingModalOpen,
    startTour,
    dismissWelcome,
    startTask,
    stopTask,
    restartTour,
    closeBookingModal,
    bookMeeting,
    closeMeetingModal,
  } = useDemoTour({ enabled: showAiDemoTours })

  const taskKeys = tasks.map((t) => t.taskKey)
  const allCompleted = tasks.length > 0 && taskKeys.every((key) => completedTasks.includes(key))

  useEffect(() => {
    if (view === 'welcome') {
      track(events.viewDemoTourModal)
    }
  }, [view])

  if (!showAiDemoTours || tasks.length === 0) return null

  return (
    <>
      {showConfetti ? <Confetti /> : null}

      {view === 'welcome' ? (
        <WelcomeModal onStart={startTour} onDismiss={dismissWelcome} onClose={dismissWelcome} />
      ) : null}

      {view === 'booking' ? (
        <BookingModal
          allCompleted={allCompleted}
          onContinue={allCompleted ? null : closeBookingModal}
          onBook={bookMeeting}
          onClose={closeBookingModal}
        />
      ) : null}

      {meetingModalOpen && calendarUrl ? (
        <Suspense fallback={null}>
          <UpsellMeeting
            isOpen
            onClose={closeMeetingModal}
            meetingUrl={calendarUrl}
            trackingSource="demo_tour"
            requestSource="From demo tour booking modal"
            skipCloseModal
            upsellRequestType="Book a meeting demo tour"
          />
        </Suspense>
      ) : null}

      {view === 'touring' || view === 'booking' ? (
        <DemoTourPopover
          tasks={tasks}
          completedTasks={completedTasks}
          activeTaskKey={activeTaskKey}
          allCompleted={allCompleted}
          currentStep={currentStep}
          onStartTask={startTask}
          onStopTask={stopTask}
          onRestart={restartTour}
          onBookMeeting={bookMeeting}
        />
      ) : null}
    </>
  )
}

export default DemoAiTours
